// Setup → AI management → Usage: the GET /api/ai/usage call (apps/server/src/aiUsage.mjs)
// and its mapping onto the @olytic/foundation/ai-admin components' props.
// Free of React, so it is unit tested (aiUsageApi.test.ts); useAiUsage.ts is the hook.
import type { AiRun, AmountFormat, UsagePoint, UsageRange, UsageRow, UsageTotals } from '@olytic/foundation/ai-admin';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** One capability's tokens and calls in the range, and its tokens in the range before. */
export interface UsageCapabilityFigures {
  id: string;
  label: string;
  amount: number;
  runs: number;
  prior: number;
}

/** One model call, newest first. `status` is `success` or `error` (migration 0066). */
export interface UsageCall {
  id: string;
  at: string;
  capability: string;
  status: string;
  amount: number;
  durationMs: number;
}

/** The body of GET /api/ai/usage. Amounts are tokens (input plus output). */
export interface UsageResponse {
  range: UsageRange;
  from: string;
  to: string;
  series: UsagePoint[];
  totals: { used: number; runs: number };
  prior: { used: number; runs: number };
  byCapability: UsageCapabilityFigures[];
  recent: UsageCall[];
}

/** What the Usage page hands to UsageOverview, UsageBreakdown and AiRunLog. */
export interface UsageView {
  series: UsagePoint[];
  totals: UsageTotals;
  prior: UsageTotals;
  breakdown: UsageRow[];
  runs: AiRun[];
  /** No model call in the range: the page shows zeros and says so. */
  empty: boolean;
}

/** The one dimension the breakdown offers: usage is not recorded per person (MOTHER-53). */
export const USAGE_DIMENSIONS = [{ key: 'capability', label: 'Capability' }] as const;

const tokenCount = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
/**
 * "12,345": a whole number of tokens. No unit after it, because the chart's axis
 * uses the same format and a unit there clips; the page says once that usage is
 * counted in tokens. (The components' default would say "credits".)
 */
export const formatTokens: AmountFormat = (amount) => tokenCount.format(amount);

/** "7 days", for the range's empty-state message. */
export const rangeLabel = (range: UsageRange): string => `${range.slice(0, -1)} days`;

/** The server's usage statuses onto the run log's: a call that errored failed. */
const runStatus = (status: string): AiRun['status'] => (status === 'success' ? 'success' : 'failed');

/**
 * The API's answer as the components' props. Active users are left out (no person
 * is recorded), and so is each run's user, so the run log drops its User column
 * rather than naming anyone.
 */
export function usageView(body: UsageResponse): UsageView {
  return {
    series: body.series.map(({ date, amount }) => ({ date, amount })),
    totals: { used: body.totals.used, runs: body.totals.runs },
    prior: { used: body.prior.used, runs: body.prior.runs },
    breakdown: body.byCapability.map((c) => ({
      id: c.id,
      label: c.label,
      // The capability's key, as the run log shows it.
      ...(c.label === c.id ? {} : { sublabel: c.id }),
      amount: c.amount,
      runs: c.runs,
      prior: c.prior,
    })),
    runs: body.recent.map((call) => ({
      id: call.id,
      at: call.at,
      capability: call.capability,
      status: runStatus(call.status),
      amount: call.amount,
      durationMs: call.durationMs,
    })),
    empty: body.totals.runs === 0,
  };
}

/** GET /api/ai/usage?range=…; an error carries the server's message when it sends one. */
export async function fetchAiUsage(fetcher: Fetch, range: UsageRange, init?: RequestInit): Promise<UsageResponse> {
  const response = await fetcher(`/api/ai/usage?range=${encodeURIComponent(range)}`, init);
  if (!response.ok) {
    let message = 'Could not load AI usage.';
    try {
      const payload = (await response.json()) as { error?: unknown };
      if (typeof payload.error === 'string') message = payload.error;
    } catch {
      // Not JSON.
    }
    throw new Error(message);
  }
  return (await response.json()) as UsageResponse;
}
